/* eslint-disable max-lines */
/**
 * WorkerLanes — per-subagent lane view of the activity stream.
 *
 * Lanes group events by (source, dispatch_id) so each scout/worker/fact-checker
 * has its own collapsed-by-default panel showing its current tool, elapsed time,
 * and status. Expanding a lane reveals its full event log with per-tool detail
 * lines (the user has signalled intent by clicking in).
 *
 * Lane click → artifact focus correlation
 * ---------------------------------------
 * Status rows resolve a click to a center-column artifact via
 * ``getArtifactIdFromLaneItem``, which reads ``result_summary.artifact_id``.
 * The backend wires this id on the status events that semantically point at a
 * stable artifact (plan-ready, deliverable-ready, plan-revising,
 * todo-execution-retry, synthesis-retry when a deliverable already exists).
 *
 * A handful of status events intentionally stay noop on click because they
 * fire at a moment where no stable artifact target exists. Listed here so
 * future contributors don't waste cycles trying to wire them; cross-references
 * are line numbers in ``app/backend/approaches/explorerdeep/streaming.py``:
 *
 *   - ``deep_workflow_restart`` (streaming.py ~L2952): pre-plan-emit signal
 *     fired after the agent bypassed the workflow this turn. Any plan id in
 *     ``_running_plan_ids`` would be stale (from a previous turn) and clicking
 *     it would jump to an unrelated old plan — worse than noop.
 *   - ``deep_synthesis_retry`` (streaming.py ~L3182) on the
 *     ``missing_deliverable`` path: no /deliverable.md has been written yet,
 *     so there is no deliverable artifact to point at. The wired stub-
 *     deliverable variant of this same event DOES carry artifact_id — only
 *     the missing-deliverable case is noop.
 */
import { useEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import type { DeepActivityEvent, OrchestratorReasoningBlock } from "@/stores/ai-explorer-store"
import type { TFunction } from "i18next"

import {
  Brain,
  Check,
  ChevronDown,
  ChevronRight,
  Loader2,
  ShieldCheck,
  Target,
  Telescope,
  Wrench,
} from "@/components/icons"
import { CATEGORY_TONES } from "@/components/ui/tone-classes"
import { useCurrentSessionType } from "@/hooks/use-current-session-type"
import { cn } from "@/lib/utils"
import { useAIExplorerStore } from "@/stores/ai-explorer-store"

import {
  buildToolDetailLines,
  extractQualityReviewBlock,
  extractSearchPreviews,
  extractSubagentReport,
  extractTaskBriefBlock,
} from "./activity-detail-helpers"
import { computeElapsedSeconds, formatElapsedBadge, humanizeToolCall } from "./activity-log-helpers"
import { collectSourceCards, groupSourceCardsByLane } from "./source-ribbon-utils"
import { useTypewriter } from "./use-typewriter"
import {
  buildLanes,
  getArtifactIdFromLaneItem,
  type LaneItem,
  laneStatus,
  type PairedTool,
} from "./worker-lanes-utils"

/**
 * Per-source visual identity. Each subagent type gets a glyph + colour so the
 * activity log reads at a glance ("scout is searching · worker is reading ·
 * fact-checker is verifying") instead of opaque source strings like
 * `worker#efc676`.
 */
function laneIdentity(source: string): {
  Icon: typeof Brain
  pillClass: string
} {
  switch (source) {
    case "deep_explorer":
      return {
        Icon: Brain,
        pillClass: "bg-foreground/10 text-foreground/80",
      }
    case "scout":
      return {
        Icon: Telescope,
        pillClass: CATEGORY_TONES[0],
      }
    case "fact_checker":
      return {
        Icon: ShieldCheck,
        pillClass: CATEGORY_TONES[4],
      }
    case "worker":
    default:
      return {
        Icon: Wrench,
        pillClass: CATEGORY_TONES[1],
      }
  }
}

function laneRoleLabel(source: string, t: TFunction): string {
  switch (source) {
    case "deep_explorer":
      return t("deepExplorer.main_agent")
    case "scout":
      return t("deepExplorer.role_scout", { defaultValue: "Scout" })
    case "fact_checker":
      return t("deepExplorer.role_fact_checker", { defaultValue: "Fact-checker" })
    case "worker":
      return t("deepExplorer.role_worker", { defaultValue: "Worker" })
    default:
      return source
  }
}

function pairLaneEvents(events: DeepActivityEvent[]): PairedTool[] {
  const byId = new Map<string, PairedTool>()
  const order: string[] = []
  for (const e of events) {
    // Status events are surfaced separately via collectLaneStatusEvents — they
    // are non-tool updates (replanning, errors, waiting-input) and don't pair.
    if (e.event === "status") continue
    let entry = byId.get(e.id)
    if (!entry) {
      entry = { id: e.id, tool: e.tool, source: e.source, start: null, end: null }
      byId.set(e.id, entry)
      order.push(e.id)
    }
    if (e.event === "tool_start") entry.start = e
    else entry.end = e
  }
  return order.map((id) => byId.get(id)!).filter(Boolean)
}

function collectLaneStatusEvents(events: DeepActivityEvent[]): DeepActivityEvent[] {
  return events.filter((e) => e.event === "status")
}

interface WorkerLanesProps {
  events: DeepActivityEvent[]
  /** B7: live subagent token streams keyed by `dispatch_id`. Looked up by
   *  each `task` row's dispatch id so the running subagent's reasoning
   *  surfaces live in the Report block, ahead of the buffered tool_end. */
  liveSubagentReports?: Record<string, string>
  /** B4: orchestrator reasoning blocks streamed live between tool calls.
   *  Rendered only in the `deep_explorer` lane — interleaved chronologically
   *  with the lane's tool calls so the panel reads as
   *  `tool → thought → tool → thought → tool`. */
  orchestratorReasoning?: OrchestratorReasoningBlock[]
  /** Round-4: per-subagent-dispatch reasoning blocks. Looked up by lane
   *  dispatchId so each subagent lane interleaves its own reasoning. */
  subagentReasoning?: Record<string, OrchestratorReasoningBlock[]>
  /** Round-5: when true, auto-expand the orchestrator lane once on mount /
   *  when the lane first appears. Used during pre-approval planning so the
   *  user sees the planner's reasoning without clicking. Manual collapse
   *  still wins after that. */
  autoExpandOrchestrator?: boolean
}

/**
 * Merge a paired-tool list with orchestrator reasoning blocks in chronological
 * order. Both kinds carry a millisecond timestamp; `Date.parse` of the tool
 * start (or end, if start is missing) gives the tool ts, and `startedAt` gives
 * the reasoning ts. Items without any usable timestamp fall to the end so the
 * sort is stable.
 */

function pairedTimestampMs(p: PairedTool): number {
  const startTs = p.start?.timestamp
  if (startTs) {
    const ms = Date.parse(startTs)
    if (Number.isFinite(ms)) return ms
  }
  const endTs = p.end?.timestamp
  if (endTs) {
    const ms = Date.parse(endTs)
    if (Number.isFinite(ms)) return ms
  }
  return Number.POSITIVE_INFINITY
}

function statusTimestampMs(e: DeepActivityEvent): number {
  if (e.timestamp) {
    const ms = Date.parse(e.timestamp)
    if (Number.isFinite(ms)) return ms
  }
  return Number.POSITIVE_INFINITY
}

function statusLabel(e: DeepActivityEvent, t: TFunction): string {
  const summary = e.result_summary as
    | { label?: string; translation_key?: string; level?: string }
    | undefined
  const key = summary?.translation_key
  const fallback = summary?.label ?? ""
  if (key) {
    return t(`deepExplorer.${key}`, { defaultValue: fallback })
  }
  return fallback
}

function statusLevel(e: DeepActivityEvent): "info" | "error" {
  const summary = e.result_summary as { level?: string } | undefined
  return summary?.level === "error" ? "error" : "info"
}

/**
 * Format a millisecond timestamp as a wall-clock badge for the activity log.
 * Returns `HH:MM:SS` in the user's locale (24h on most locales, 12h with am/pm
 * on en-US). Returns `null` for non-finite inputs so callers can omit the
 * badge for entries with no usable timestamp.
 */
function formatTimeOfDay(ms: number): string | null {
  if (!Number.isFinite(ms)) return null
  const d = new Date(ms)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  })
}

/**
 * Renders text with a typewriter reveal on mount. Keyed by `replayKey` so
 * each lane entry types itself exactly once even when its parent list
 * re-renders. Used for tool labels, status labels, and the collapsed-lane
 * preview line — gives the side-panel a coherent "typing" feel that mirrors
 * the streaming reasoning text.
 */
function TypewriterText({
  text,
  replayKey,
  className,
  instant,
}: {
  text: string
  replayKey: string
  className?: string
  /** When true, render the full text immediately without animating. Pass
   *  for finished content (e.g. expanded lane that has a stable phrase)
   *  so the typewriter doesn't replay every time the user re-opens the
   *  lane. */
  instant?: boolean
}) {
  const displayed = useTypewriter(text, replayKey, undefined, instant)
  return <span className={className}>{displayed}</span>
}

/**
 * Reasoning text rendering: typewriter reveals every character regardless
 * of live/done state. While live, `text` grows as chunks arrive and the
 * typewriter advances toward it — multi-character LLM token chunks no
 * longer pop in en masse, every character types in order. Once live ends,
 * the typewriter finishes the remaining tail. Inline component because
 * the hook needs a stable host.
 *
 * `instant` short-circuits the animation — pass `!block.live` so blocks
 * that finished streaming long ago appear instantly when the lane is
 * re-expanded instead of replaying from empty.
 */
function ReasoningText({
  text,
  blockId,
  instant,
}: {
  text: string
  blockId: string
  instant?: boolean
}) {
  return <>{useTypewriter(text, `reasoning-${blockId}`, undefined, instant)}</>
}

/**
 * Subagent-report text rendering: same character-by-character reveal as
 * ReasoningText, applied inside the click-to-expand `<pre>` so both the
 * live stream and any buffered final report animate in. Pass
 * `instant=true` for finished reports so re-expansion doesn't replay
 * the whole document character-by-character.
 */
function ReportText({
  text,
  reportId,
  instant,
}: {
  text: string
  reportId: string
  instant?: boolean
}) {
  return <>{useTypewriter(text, `report-${reportId}`, undefined, instant)}</>
}

/**
 * Pick the most recent meaningful preview text for a lane: latest tool
 * phrase if any tool fired, else the most recent status label, else the
 * tail of the latest reasoning chunk. Returns `null` only when the lane
 * has no displayable content yet (collapsed empty lane).
 */

// Exported for unit-test reach; pure helper doesn't reference React state,
// so the fast-refresh warning is a false positive here.
// eslint-disable-next-line react-refresh/only-export-components
export function computeLatestPhrase({
  paired,
  reasoning,
  statusEvents,
  t,
}: {
  paired: PairedTool[]
  reasoning: OrchestratorReasoningBlock[]
  statusEvents: DeepActivityEvent[]
  t: TFunction
}): string | null {
  const candidates: Array<{ ts: number; text: string }> = []
  for (const p of paired) {
    if (!p.start && !p.end) continue
    candidates.push({ ts: pairedTimestampMs(p), text: humanizeToolCall(p, t) })
  }
  // ``deep_explorer_thinking`` is emitted at every ``on_chat_model_start`` to
  // fill the silent gap during reasoning. Once a lane has actual tool work
  // (any paired tool that ran to completion), the tool labels are a much
  // better preview than a leftover "Thinking…" pill — and an idle
  // post-completion "Thinking…" status (e.g. the orchestrator's final
  // chat_model_start before stream end) would otherwise stick as the
  // collapsed preview on a DONE lane. Filter it out when there's real work
  // to show; keep it when the lane has nothing else (live planning phase).
  const hasFinishedTool = paired.some((p) => p.end)
  for (const s of statusEvents) {
    const tkey = (s.result_summary as { translation_key?: string } | undefined)?.translation_key
    if (tkey === "deep_explorer_thinking" && hasFinishedTool) continue
    const text = statusLabel(s, t)
    if (text) candidates.push({ ts: statusTimestampMs(s), text })
  }
  for (const r of reasoning) {
    const text = r.text.trim()
    if (text) {
      // For long reasoning blocks, surface only the trailing sentence-ish
      // segment so the preview line stays readable.
      const tail = text.length > 120 ? `…${text.slice(-120)}` : text
      candidates.push({ ts: r.startedAt, text: tail })
    }
  }
  if (candidates.length === 0) return null
  // Highest finite timestamp wins. Items with POSITIVE_INFINITY (no usable
  // timestamp) sort last and only surface when they're the only option.
  candidates.sort((a, b) => b.ts - a.ts)
  return candidates[0].text
}

function buildOrchestratorTimeline(
  paired: PairedTool[],
  reasoning: OrchestratorReasoningBlock[],
  statusEvents: DeepActivityEvent[] = []
): LaneItem[] {
  const items: LaneItem[] = [
    ...paired.map<LaneItem>((p) => ({ kind: "tool", ts: pairedTimestampMs(p), paired: p })),
    ...reasoning.map<LaneItem>((b) => ({ kind: "reasoning", ts: b.startedAt, block: b })),
    ...statusEvents.map<LaneItem>((s) => ({
      kind: "status",
      ts: statusTimestampMs(s),
      status: s,
    })),
  ]
  // Stable sort (Array.prototype.sort is stable in modern engines) so equal
  // timestamps preserve insertion order — keeps the user's mental model when
  // reasoning and a tool fire in the same millisecond.
  items.sort((a, b) => a.ts - b.ts)
  return items
}

function laneSurfaceClass(source: string, hasError: boolean): string {
  if (hasError) return "border-destructive/40 bg-destructive/5"
  switch (source) {
    case "deep_explorer":
      return "border-muted-foreground/30 bg-muted/20"
    case "scout":
      return "border-border bg-muted/30"
    case "fact_checker":
      return "border-border bg-muted/30"
    case "worker":
    default:
      return "border-border bg-muted/30"
  }
}

function statusBadgeClass(isRunning: boolean, hasError: boolean): string {
  if (isRunning) return "bg-primary/20 text-primary"
  if (hasError) return "bg-destructive/15 text-destructive"
  return "bg-muted text-muted-foreground"
}

function renderStatusIcon(isRunning: boolean, hasError: boolean, compact = false) {
  if (isRunning) {
    return compact ? (
      <Loader2 className="size-2.5 shrink-0 animate-spin motion-reduce:animate-none" />
    ) : (
      <Loader2 className="text-primary size-3 shrink-0 animate-spin motion-reduce:animate-none" />
    )
  }
  if (hasError) {
    return compact ? (
      <span className="text-destructive">×</span>
    ) : (
      <span className="text-destructive shrink-0 text-sm leading-none">×</span>
    )
  }
  return compact ? (
    <Check className="text-muted-foreground size-2.5 shrink-0" />
  ) : (
    <Check className="text-muted-foreground size-3 shrink-0" />
  )
}

export function WorkerLanes({
  autoExpandOrchestrator,
  events,
  liveSubagentReports,
  orchestratorReasoning,
  subagentReasoning,
}: WorkerLanesProps) {
  const { t } = useTranslation()
  // Phase 4 (workspace-focus): clicking a lane row that correlates to an
  // artifact (via `result_summary.artifact_id` or its tool-input fallback)
  // focuses that artifact in the center workspace. We resolve the namespaced
  // AI Explorer store via the route — the same lookup `ai-explorer-page` uses
  // — so this component stays decoupled from `WorkspaceLayout` and works
  // identically in legacy and workspace modes (in legacy mode, `focusArtifact`
  // writes to a store field nobody reads — harmless).
  const sessionType = useCurrentSessionType()
  const explorerStore = useAIExplorerStore(sessionType)
  const currentSessionId = explorerStore((s) => s.currentSessionId)
  const focusArtifactAction = explorerStore((s) => s.focusArtifact)
  const handleFocusArtifact = (artifactId: string) => {
    if (!currentSessionId) return
    focusArtifactAction(currentSessionId, artifactId)
  }
  // Lanes start collapsed and only open on user click — auto-expand was
  // pulling the user's eye into running subagents whose details they
  // didn't ask to see (2026-05-04 retest).
  // Exception (round-5): when `autoExpandOrchestrator` is true, the
  // orchestrator (`deep_explorer`) lane is opened once on mount so the
  // pre-approval planning prose is visible without a click. Tracked by
  // ref so a manual collapse during the same planning phase still wins.
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  const orchestratorAutoExpandedRef = useRef(false)
  useEffect(() => {
    if (!autoExpandOrchestrator) {
      orchestratorAutoExpandedRef.current = false
      return
    }
    if (orchestratorAutoExpandedRef.current) return
    orchestratorAutoExpandedRef.current = true
    setExpanded((prev) => {
      if (prev.has("deep_explorer")) return prev
      const next = new Set(prev)
      next.add("deep_explorer")
      return next
    })
  }, [autoExpandOrchestrator])
  // Per-pair toggle for the Report block (subagent output_text).
  const [openReports, setOpenReports] = useState<Set<string>>(() => new Set())
  // Per-pair toggle for the search-results expand block (B1).
  const [openPreviews, setOpenPreviews] = useState<Set<string>>(() => new Set())
  // Per-pair toggle for the task-brief expand block (B3).
  const [openBriefs, setOpenBriefs] = useState<Set<string>>(() => new Set())
  // Per-pair toggle for the QA verdict expand block — write_file /
  // read_file of /quality_review.json carry the verdict JSON in
  // ``result_summary.quality_review_content`` and the user can click to
  // inspect passes/score/issues inline (mirror of the task-brief
  // pattern, Erik feedback 2026-05-21).
  const [openVerdicts, setOpenVerdicts] = useState<Set<string>>(() => new Set())
  const lanes = buildLanes(events)
  // Synthesize an orchestrator lane if the planner is streaming reasoning
  // before any tool events have built the real lane. Without this, pre-
  // approval planner prose has no visible home in the side panel.
  const hasReasoning = (orchestratorReasoning?.length ?? 0) > 0
  const hasOrchestratorLane = lanes.some((l) => l.source === "deep_explorer")
  if (hasReasoning && !hasOrchestratorLane) {
    lanes.unshift({
      key: "deep_explorer",
      source: "deep_explorer",
      dispatchId: null,
      events: [],
    })
  }

  if (lanes.length === 0) {
    return (
      <p className="text-muted-foreground py-3 text-center text-xs">
        {t("deepExplorer.no_activity_yet")}
      </p>
    )
  }

  const toggleReport = (id: string) =>
    setOpenReports((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const togglePreviews = (id: string) =>
    setOpenPreviews((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const toggleBrief = (id: string) =>
    setOpenBriefs((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const toggleVerdict = (id: string) =>
    setOpenVerdicts((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  // A2: when at least one lane is running, dim the inactive lanes so the
  // user's eye snaps to motion. Computed once per render rather than per-lane.
  const anyRunning = lanes.some((lane) => laneStatus(pairLaneEvents(lane.events)).running > 0)
  // B6: per-lane source attribution. Compute once and look up by lane key
  // so each lane shows only the sources its dispatch actually retrieved.
  const sourcesByLane = groupSourceCardsByLane(collectSourceCards(events))

  return (
    <ul
      aria-label={t("deepExplorer.worker_lanes_aria", { defaultValue: "Worker lanes" })}
      className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-1.5 py-1"
    >
      {lanes.map((lane) => {
        const paired = pairLaneEvents(lane.events)
        const laneStatusEvents = collectLaneStatusEvents(lane.events)
        const status = laneStatus(paired)
        const isMain = lane.source === "deep_explorer"
        const isRunning = status.running > 0
        // Lanes are user-controlled only — running lanes no longer auto-open.
        // Auto-expansion was pulling the user's eye into details they hadn't
        // asked to see (2026-05-04 retest).
        const isOpen = expanded.has(lane.key)
        const identity = laneIdentity(lane.source)
        const RoleIcon = identity.Icon
        const roleLabel = laneRoleLabel(lane.source, t)
        const dispatchSuffix = !isMain && lane.dispatchId ? `#${lane.dispatchId.slice(0, 6)}` : null
        const elapsedBadge = formatElapsedBadge(status.elapsedSeconds)
        const hasError = status.errored > 0
        const laneReasoning = isMain
          ? (orchestratorReasoning ?? [])
          : (lane.dispatchId && subagentReasoning?.[lane.dispatchId]) || []
        const latestPhrase = computeLatestPhrase({
          paired,
          reasoning: laneReasoning,
          statusEvents: laneStatusEvents,
          t,
        })

        return (
          <li
            key={lane.key}
            id={`worker-lane-${lane.key}`}
            className={cn(
              "animate-in fade-in-0 slide-in-from-top-1 relative scroll-mt-2 rounded-md border px-2 py-1.5 duration-300 ease-out",
              "transition-[box-shadow,opacity,border-color] motion-reduce:transition-none",
              laneSurfaceClass(lane.source, hasError),
              // Dim inactive lanes when something is running so the active
              // worker stands out. `motion-reduce:opacity-100` respects users
              // who've turned animation off.
              anyRunning && !isRunning && "opacity-50 motion-reduce:opacity-100",
              // Bolder running indicator: a thicker pulsing stripe + soft
              // primary glow so the eye snaps to the active lane (round-2
              // feedback — original 3px stripe was too subtle).
              isRunning &&
                "before:bg-primary border-primary/60 shadow-primary/15 shadow-md before:absolute before:top-1 before:bottom-1 before:left-0 before:w-1 before:animate-pulse before:rounded-r-md motion-reduce:before:animate-none"
            )}
          >
            <button
              aria-expanded={isOpen}
              className="flex w-full items-center gap-2 text-left text-xs"
              type="button"
              onClick={() => {
                setExpanded((prev) => {
                  const next = new Set(prev)
                  if (next.has(lane.key)) next.delete(lane.key)
                  else next.add(lane.key)
                  return next
                })
              }}
            >
              {isOpen ? (
                <ChevronDown className="size-3 shrink-0" />
              ) : (
                <ChevronRight className="size-3 shrink-0" />
              )}
              {renderStatusIcon(isRunning, hasError)}
              <span
                className={cn(
                  "inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold",
                  identity.pillClass
                )}
              >
                <RoleIcon className="size-3 shrink-0" />
                {roleLabel}
              </span>
              {dispatchSuffix && (
                <span className="text-muted-foreground/70 shrink-0 font-mono text-[10px] tabular-nums">
                  {dispatchSuffix}
                </span>
              )}
              <span className="text-muted-foreground shrink-0 text-[10px] tabular-nums">
                {status.total} {t("deepExplorer.call", { count: status.total })}
              </span>
              {elapsedBadge && (
                <span className="text-muted-foreground/80 shrink-0 text-[10px] tabular-nums">
                  · {elapsedBadge}
                </span>
              )}
              <span
                className={cn(
                  "ml-auto shrink-0 rounded-full px-1.5 py-px text-[10px] font-medium tracking-wide uppercase",
                  statusBadgeClass(isRunning, hasError)
                )}
              >
                {isRunning && t("deepExplorer.lane_status_running", { defaultValue: "running" })}
                {!isRunning &&
                  hasError &&
                  t("deepExplorer.lane_status_errored", { defaultValue: "errored" })}
                {!isRunning &&
                  !hasError &&
                  t("deepExplorer.lane_status_done", { defaultValue: "done" })}
              </span>
            </button>
            {!isOpen && latestPhrase && (
              <p
                key={`preview-${lane.key}-${latestPhrase}`}
                className="text-muted-foreground mt-0.5 ml-5 truncate text-[11px]"
              >
                <TypewriterText
                  // Animate while the lane is running; once it's done
                  // the preview reflects a finished tool call and
                  // shouldn't replay every render.
                  instant={!isRunning}
                  replayKey={`preview-${lane.key}-${latestPhrase}`}
                  text={latestPhrase}
                />
              </p>
            )}
            {isOpen && (
              <div className="animate-in fade-in-0 slide-in-from-top-1 overflow-hidden duration-200 ease-out motion-reduce:animate-none">
                <ul className="mt-1 ml-3 flex flex-col gap-0">
                  {buildOrchestratorTimeline(paired, laneReasoning, laneStatusEvents).map(
                    (item) => {
                      if (item.kind === "status") {
                        const label = statusLabel(item.status, t)
                        if (!label) return null
                        const level = statusLevel(item.status)
                        const timeBadge = formatTimeOfDay(item.ts)
                        // Phase 4 (workspace-focus): status events emitted at
                        // plan-ready / deliverable-ready sites carry an
                        // `artifact_id` on `result_summary` (Phase 2.5). Click
                        // → focus that artifact in the center workspace.
                        const focusArtifactId = getArtifactIdFromLaneItem(item)
                        const focusable = focusArtifactId !== null
                        const focusLabel = t("deepExplorer.focus_artifact", {
                          defaultValue: "Open artifact in workspace",
                        })
                        return (
                          // <li> with role="button" is semantically interactive when focusable;
                          // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                          <li
                            key={`status-${item.status.id}`}
                            aria-label={focusable ? focusLabel : undefined}
                            data-focusable={focusable ? "true" : "false"}
                            role={focusable ? "button" : undefined}
                            tabIndex={focusable ? 0 : undefined}
                            className={cn(
                              "animate-in fade-in-0 slide-in-from-left-1 mt-0.5 flex items-start gap-1.5 rounded-sm py-0.5 text-[11px] transition-colors duration-300 ease-out",
                              level === "error" ? "text-destructive" : "text-muted-foreground/90",
                              focusable &&
                                "hover:bg-accent/50 focus-visible:ring-ring -mx-1 cursor-pointer px-1 focus-visible:ring-2 focus-visible:outline-none motion-reduce:transition-none"
                            )}
                            onClick={
                              focusable && focusArtifactId
                                ? () => handleFocusArtifact(focusArtifactId)
                                : undefined
                            }
                            onKeyDown={
                              focusable && focusArtifactId
                                ? (e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault()
                                      handleFocusArtifact(focusArtifactId)
                                    }
                                  }
                                : undefined
                            }
                          >
                            <span
                              aria-hidden="true"
                              className={cn(
                                "mt-1 inline-block size-1.5 shrink-0 rounded-full",
                                level === "error" ? "bg-destructive" : "bg-primary/60"
                              )}
                            />
                            <TypewriterText
                              // Status events are emitted as discrete,
                              // fully-formed payloads — never streamed —
                              // so re-expanding the lane shouldn't replay
                              // each label character-by-character.
                              instant
                              className="min-w-0 flex-1 leading-snug"
                              replayKey={`status-${item.status.id}`}
                              text={label}
                            />
                            {timeBadge && (
                              <time
                                className="text-muted-foreground/60 shrink-0 font-mono text-[10px] tabular-nums"
                                dateTime={new Date(item.ts).toISOString()}
                              >
                                {timeBadge}
                              </time>
                            )}
                            {focusable && (
                              <Target
                                aria-hidden
                                className="text-muted-foreground/60 size-3 shrink-0"
                                data-testid="lane-entry-focus-icon"
                              />
                            )}
                          </li>
                        )
                      }
                      if (item.kind === "reasoning") {
                        const block = item.block
                        const text = block.text
                        const timeBadge = formatTimeOfDay(item.ts)
                        return (
                          <li
                            key={`reasoning-${block.id}`}
                            className="animate-in fade-in-0 slide-in-from-left-1 text-muted-foreground/90 mt-0.5 flex flex-col gap-0.5 py-0.5 text-[11px] italic duration-300 ease-out"
                          >
                            <div className="flex items-start gap-1.5">
                              <Brain className="text-primary/80 mt-0.5 size-3 shrink-0" />
                              {timeBadge && (
                                <time
                                  className="text-muted-foreground/60 mt-0.5 shrink-0 font-mono text-[10px] not-italic tabular-nums"
                                  dateTime={new Date(item.ts).toISOString()}
                                >
                                  {timeBadge}
                                </time>
                              )}
                              <span className="bg-muted/40 border-border min-w-0 flex-1 rounded-md border px-2 py-1 leading-snug whitespace-pre-wrap transition-colors duration-300">
                                <ReasoningText
                                  blockId={block.id}
                                  // Animate only while the block is
                                  // streaming. Once `live` flips false on
                                  // _reasoning_complete the text is final
                                  // and re-expanding the lane should show
                                  // it instantly, not replay typing.
                                  instant={!block.live}
                                  text={text}
                                />
                                {block.live && (
                                  <span className="text-primary/80 ml-0.5 inline-block animate-pulse motion-reduce:animate-none">
                                    ▍
                                  </span>
                                )}
                              </span>
                              {block.live && (
                                <span
                                  className="bg-primary/15 text-primary mt-0.5 inline-flex shrink-0 animate-pulse items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tracking-wide uppercase motion-reduce:animate-none"
                                  aria-label={t("deepExplorer.reasoning_aria", {
                                    defaultValue: "Orchestrator reasoning, streaming",
                                  })}
                                >
                                  <span className="bg-primary size-1 rounded-full" />
                                  {t("deepExplorer.reasoning_thinking", {
                                    defaultValue: "Thinking",
                                  })}
                                </span>
                              )}
                            </div>
                          </li>
                        )
                      }
                      const p = item.paired
                      const phrase = humanizeToolCall(p, t)
                      const elapsed = formatElapsedBadge(computeElapsedSeconds(p.start, p.end))
                      const timeBadge = formatTimeOfDay(item.ts)
                      const running = !p.end
                      const error = p.end?.result_summary?.success === false
                      const details = buildToolDetailLines(p, t)
                      const report = extractSubagentReport(p, liveSubagentReports)
                      const reportOpen = openReports.has(p.id)
                      // B7: while the subagent is streaming live, default-open
                      // the block so the user sees the text without clicking.
                      // Manual collapse via toggleReport still wins the moment
                      // the user expresses intent.
                      const reportOpenEffective = reportOpen || (report?.live ?? false)
                      const previews = extractSearchPreviews(p)
                      const previewsOpen = openPreviews.has(p.id)
                      const brief = extractTaskBriefBlock(p)
                      const briefOpen = openBriefs.has(p.id)
                      const verdict = extractQualityReviewBlock(p)
                      const verdictOpen = openVerdicts.has(p.id)
                      // Phase 4 (workspace-focus): row is clickable iff the
                      // tool call carries a correlatable `artifact_id`.
                      const focusArtifactId = getArtifactIdFromLaneItem(item)
                      const focusable = focusArtifactId !== null
                      const focusLabel = t("deepExplorer.focus_artifact", {
                        defaultValue: "Open artifact in workspace",
                      })
                      return (
                        <li
                          key={p.id}
                          className={cn(
                            "animate-in fade-in-0 slide-in-from-left-1 flex flex-col gap-0.5 py-0.5 text-[11px] duration-300 ease-out",
                            error && "text-destructive"
                          )}
                        >
                          <div
                            aria-label={focusable ? focusLabel : undefined}
                            data-focusable={focusable ? "true" : "false"}
                            role={focusable ? "button" : undefined}
                            tabIndex={focusable ? 0 : undefined}
                            className={cn(
                              "flex items-start gap-1.5 rounded-sm transition-colors",
                              focusable &&
                                "hover:bg-accent/50 focus-visible:ring-ring -mx-1 cursor-pointer px-1 focus-visible:ring-2 focus-visible:outline-none motion-reduce:transition-none"
                            )}
                            onClick={
                              focusable && focusArtifactId
                                ? () => handleFocusArtifact(focusArtifactId)
                                : undefined
                            }
                            onKeyDown={
                              focusable && focusArtifactId
                                ? (e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault()
                                      handleFocusArtifact(focusArtifactId)
                                    }
                                  }
                                : undefined
                            }
                          >
                            {renderStatusIcon(running, error, true)}
                            <TypewriterText
                              // Animate the tool phrase while the call is
                              // in flight; once tool_end has landed
                              // (`!running`) the phrase is final and
                              // shouldn't re-type on every re-render.
                              className="min-w-0 flex-1 truncate"
                              instant={!running}
                              replayKey={`tool-${p.id}`}
                              text={phrase}
                            />
                            {elapsed && (
                              <span className="text-muted-foreground/70 shrink-0 text-[10px] tabular-nums">
                                {elapsed}
                              </span>
                            )}
                            {timeBadge && (
                              <time
                                className="text-muted-foreground/60 shrink-0 font-mono text-[10px] tabular-nums"
                                dateTime={new Date(item.ts).toISOString()}
                              >
                                {timeBadge}
                              </time>
                            )}
                            {focusable && (
                              <Target
                                aria-hidden
                                className="text-muted-foreground/60 size-3 shrink-0"
                                data-testid="lane-entry-focus-icon"
                              />
                            )}
                          </div>
                          {details.length > 0 && (
                            <span className="text-muted-foreground/80 ml-4 flex min-w-0 flex-col gap-0.5 text-[10px] leading-snug">
                              {details.map((line) => (
                                // line-clamp-2 (not truncate) so the richer detail
                                // lines — Failed:, Top result: — can show up to two
                                // lines of meaningful content. Short lines fall
                                // back to a single line naturally.
                                <span key={line} className="line-clamp-2">
                                  {line}
                                </span>
                              ))}
                            </span>
                          )}
                          {brief && (
                            <div className="mt-1 ml-4 flex flex-col gap-1">
                              <button
                                aria-expanded={briefOpen}
                                className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 self-start text-[10px] font-medium underline-offset-2 hover:underline"
                                type="button"
                                onClick={() => toggleBrief(p.id)}
                              >
                                {briefOpen ? (
                                  <ChevronDown className="size-3 shrink-0" />
                                ) : (
                                  <ChevronRight className="size-3 shrink-0" />
                                )}
                                {briefOpen
                                  ? t("deepExplorer.hide_brief", { defaultValue: "Hide brief" })
                                  : t("deepExplorer.show_brief", { defaultValue: "Show brief" })}
                                {!briefOpen && (
                                  <span className="text-muted-foreground/70 ml-1 truncate font-normal">
                                    · {brief.preview}
                                  </span>
                                )}
                              </button>
                              {briefOpen && (
                                <pre className="bg-muted/40 border-border max-h-72 overflow-y-auto rounded-md border px-2 py-1.5 text-[10px] leading-relaxed break-words whitespace-pre-wrap">
                                  {brief.full}
                                </pre>
                              )}
                            </div>
                          )}
                          {verdict && (
                            <div className="mt-1 ml-4 flex flex-col gap-1">
                              <button
                                aria-expanded={verdictOpen}
                                className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 self-start text-[10px] font-medium underline-offset-2 hover:underline"
                                type="button"
                                onClick={() => toggleVerdict(p.id)}
                              >
                                {verdictOpen ? (
                                  <ChevronDown className="size-3 shrink-0" />
                                ) : (
                                  <ChevronRight className="size-3 shrink-0" />
                                )}
                                {verdictOpen
                                  ? t("deepExplorer.hide_verdict", {
                                      defaultValue: "Hide verdict",
                                    })
                                  : t("deepExplorer.show_verdict", {
                                      defaultValue: "Show verdict",
                                    })}
                                {!verdictOpen && (
                                  <span className="text-muted-foreground/70 ml-1 truncate font-normal">
                                    · {verdict.preview}
                                  </span>
                                )}
                              </button>
                              {verdictOpen && (
                                <pre
                                  className="bg-muted/40 border-border max-h-72 overflow-y-auto rounded-md border px-2 py-1.5 text-[10px] leading-relaxed break-words whitespace-pre-wrap"
                                  data-testid="lane-entry-quality-review"
                                >
                                  {verdict.full}
                                </pre>
                              )}
                            </div>
                          )}
                          {previews && (
                            <div className="mt-1 ml-4 flex flex-col gap-1">
                              <button
                                aria-expanded={previewsOpen}
                                className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 self-start text-[10px] font-medium underline-offset-2 hover:underline"
                                type="button"
                                onClick={() => togglePreviews(p.id)}
                              >
                                {previewsOpen ? (
                                  <ChevronDown className="size-3 shrink-0" />
                                ) : (
                                  <ChevronRight className="size-3 shrink-0" />
                                )}
                                {previewsOpen
                                  ? t("deepExplorer.hide_results", { defaultValue: "Hide results" })
                                  : t("deepExplorer.show_results", {
                                      defaultValue: "Show {{count}} results",
                                      count: previews.length,
                                    })}
                              </button>
                              {previewsOpen && (
                                <ol className="border-border bg-muted/40 flex flex-col gap-1.5 rounded-md border px-2 py-1.5 text-[10px] leading-snug">
                                  {previews.map((item, idx) => (
                                    <li
                                      key={`${p.id}-preview-${idx}`}
                                      className="flex flex-col gap-0.5"
                                    >
                                      {item.title && (
                                        <span className="text-foreground line-clamp-1 font-medium">
                                          {idx + 1}. {item.title}
                                        </span>
                                      )}
                                      <span className="text-muted-foreground line-clamp-3">
                                        {item.snippet}
                                      </span>
                                      {item.sourceId && (
                                        <span className="text-muted-foreground/70 truncate font-mono text-[10px]">
                                          {item.sourceId}
                                        </span>
                                      )}
                                    </li>
                                  ))}
                                </ol>
                              )}
                            </div>
                          )}
                          {report && (
                            <div className="mt-1 ml-4 flex flex-col gap-1">
                              <button
                                aria-expanded={reportOpenEffective}
                                className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 self-start text-[10px] font-medium underline-offset-2 hover:underline"
                                type="button"
                                onClick={() => toggleReport(p.id)}
                              >
                                {reportOpenEffective ? (
                                  <ChevronDown className="size-3 shrink-0" />
                                ) : (
                                  <ChevronRight className="size-3 shrink-0" />
                                )}
                                {report.live && (
                                  <span className="bg-primary/15 text-primary inline-flex animate-pulse items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tracking-wide uppercase motion-reduce:animate-none">
                                    <span className="bg-primary size-1 rounded-full" />
                                    {t("deepExplorer.live_report", { defaultValue: "Live" })}
                                  </span>
                                )}
                                {reportOpenEffective
                                  ? t("deepExplorer.hide_report", { defaultValue: "Hide report" })
                                  : t("deepExplorer.show_report", { defaultValue: "Show report" })}
                                {!reportOpenEffective && (
                                  <span className="text-muted-foreground/70 ml-1 truncate font-normal">
                                    · {report.preview}
                                  </span>
                                )}
                              </button>
                              {reportOpenEffective && (
                                <pre className="bg-muted/40 border-border max-h-72 overflow-y-auto rounded-md border px-2 py-1.5 text-[10px] leading-relaxed break-words whitespace-pre-wrap">
                                  <ReportText
                                    // Animate while the report is still
                                    // streaming; finished reports show
                                    // instantly when the user re-opens
                                    // the toggle (otherwise re-expansion
                                    // replays every character).
                                    instant={!report.live}
                                    reportId={p.id}
                                    text={report.full}
                                  />
                                  {report.live && (
                                    <span className="text-primary/80 ml-0.5 inline-block animate-pulse motion-reduce:animate-none">
                                      ▍
                                    </span>
                                  )}
                                </pre>
                              )}
                            </div>
                          )}
                        </li>
                      )
                    }
                  )}
                </ul>
                {(() => {
                  const laneSources = sourcesByLane.get(lane.key) ?? []
                  if (laneSources.length === 0) return null
                  return (
                    <div className="mt-1 ml-3 flex flex-col gap-1">
                      <span className="text-muted-foreground/80 text-[10px] font-medium tracking-wide uppercase">
                        {t("deepExplorer.lane_sources", {
                          defaultValue: "Sources from this agent",
                        })}{" "}
                        · {laneSources.length}
                      </span>
                      <ul className="border-border bg-muted/30 flex flex-col gap-0.5 rounded-md border px-2 py-1.5 text-[10px] leading-snug">
                        {laneSources.map((source) => (
                          <li key={source.id} className="flex flex-col">
                            <span className="text-foreground line-clamp-1 font-medium">
                              {source.title}
                            </span>
                            {(source.library || source.sourcefile || source.url) && (
                              <span className="text-muted-foreground/70 truncate font-mono text-[10px]">
                                {[source.library, source.sourcefile].filter(Boolean).join(":") ||
                                  source.url}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )
                })()}
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
}
